/**
 * Elegir día y hora mirando la agenda real del profesional.
 *
 * Una sola forma de dar una hora en todo el sistema. Antes había dos: un
 * calendario con el día abierto al lado (para las sesiones de un tratamiento) y
 * una rejilla de botones "10:00–10:45" apretados bajo un campo de fecha (para
 * las citas). La segunda obligaba a adivinar en qué días atiende el profesional
 * —había que ir probando fechas— y no decía nada de lo que ya tenía ocupado.
 *
 * Aquí se ve el mes con sus días hábiles y su carga, y al entrar en un día, su
 * jornada entera: lo libre se pulsa, lo ocupado se muestra con el nombre de
 * quien lo ocupa. Se enseña lo ocupado a propósito: "libre a las 10" se decide
 * distinto si a las 9:30 hay alguien.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  type SlotLibre,
  listBloqueos,
  listReglas,
  listSlotsLibres,
} from "../../api/agenda";
import { listCitas } from "../../api/citas";
import { ApiError } from "../../api/client";
import { useZonaHoraria } from "../../auth/TenantBrandingProvider";
import { hora as horaDe } from "../../lib/fecha";
import {
  type MarcaDia,
  CalendarioDisponibilidad,
  proximoDiaDisponible,
} from "../CalendarioDisponibilidad";
import { Alert } from "../ui";

/** "YYYY-MM-DD" de hoy en la zona del policlínico. */
export function hoyEnZona(tz: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

interface Franja {
  inicio: string;
  fin: string;
  ocupada: boolean;
  quien?: string;
  pagada?: boolean;
  sesion?: number | null;
  slot?: SlotLibre;
}

export function SelectorAgenda({
  profesionalId,
  sedeId,
  dia,
  onDiaChange,
  onElegirSlot,
  slotElegidoInicio,
  planId,
  deshabilitado,
}: {
  profesionalId: number | null;
  sedeId?: number | null;
  /** "YYYY-MM-DD" visible. */
  dia: string;
  onDiaChange: (dia: string) => void;
  onElegirSlot: (slot: SlotLibre) => void;
  /** ISO del slot marcado, cuando el padre solo selecciona en vez de actuar. */
  slotElegidoInicio?: string | null;
  /** Rotula como "S1", "S2"… las citas de este tratamiento. */
  planId?: number | null;
  deshabilitado?: boolean;
}) {
  const tz = useZonaHoraria();
  const hoy = hoyEnZona(tz);
  const activo = profesionalId != null && !deshabilitado;

  const {
    data: reglas = [],
    isLoading: cargandoReglas,
    error: errorReglas,
  } = useQuery({
    queryKey: ["agenda", "reglas", profesionalId],
    queryFn: () => listReglas(profesionalId as number),
    enabled: activo,
    retry: false,
  });
  const { data: bloqueos = [] } = useQuery({
    queryKey: ["agenda", "bloqueos", profesionalId, hoy],
    queryFn: () => listBloqueos(profesionalId as number, hoy),
    enabled: activo,
  });

  // Sin `profesional:read_all` no se puede mirar la agenda de un colega. Ese
  // 403 llegaba como lista vacía y la pantalla concluía "no tiene horario",
  // que es falso y manda a configurar algo que ya existe.
  const sinAcceso = errorReglas instanceof ApiError && errorReglas.status === 403;

  const [mesVisible, setMesVisible] = useState(() => dia.slice(0, 7));
  const { data: citasDelMes = [] } = useQuery({
    queryKey: ["citas", "mes", profesionalId, mesVisible],
    queryFn: () => {
      const [a, m] = mesVisible.split("-").map(Number);
      // El tope es el día 1 del mes siguiente: "hasta el 31" se lee como su
      // medianoche y se perderían las citas del último día.
      const sig = new Date(a, m, 1);
      const tope = `${sig.getFullYear()}-${String(sig.getMonth() + 1).padStart(2, "0")}-01`;
      return listCitas({
        profesional_id: profesionalId as number,
        desde: `${mesVisible}-01`,
        hasta: tope,
      });
    },
    enabled: activo,
  });

  const diaDe = (iso: string) =>
    new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(iso));

  const marcasPorDia: Record<string, MarcaDia> = {};
  for (const c of citasDelMes) {
    const d = diaDe(c.inicio);
    const m = (marcasPorDia[d] ??= { pagadas: 0, porCobrar: 0 });
    if (c.pago_estado === "PAGADO") m.pagadas += 1;
    else m.porCobrar += 1;
    if (planId != null && c.plan_id === planId && c.numero_sesion) {
      m.sesion = c.numero_sesion;
    }
  }

  const { data: slots = [], isFetching: cargandoSlots } = useQuery({
    queryKey: ["agenda", "slots", profesionalId, dia, sedeId],
    queryFn: () => listSlotsLibres(profesionalId as number, dia, sedeId),
    enabled: activo && !!dia,
  });
  const { data: citasDelDia = [] } = useQuery({
    queryKey: ["citas", "dia", profesionalId, dia],
    queryFn: () => listCitas({ profesional_id: profesionalId as number, fecha: dia }),
    enabled: activo && !!dia,
  });

  // Libre y ocupado en una sola línea de tiempo: es como se lee un día.
  const franjas: Franja[] = [
    ...slots.map((s) => ({
      inicio: s.inicio,
      fin: s.fin,
      ocupada: false,
      slot: s,
    })),
    ...citasDelDia.map((c) => ({
      inicio: c.inicio,
      fin: c.fin,
      ocupada: true,
      quien: c.paciente_nombre,
      pagada: c.pago_estado === "PAGADO",
      sesion: planId != null && c.plan_id === planId ? c.numero_sesion : null,
    })),
  ].sort((a, b) => a.inicio.localeCompare(b.inicio));

  // Si el día de partida no es hábil, se salta al primero que sí lo sea:
  // abrir en un día imposible obliga a buscar a mano.
  const [ajustado, setAjustado] = useState(false);
  if (activo && !ajustado && !cargandoReglas && reglas.length > 0) {
    setAjustado(true);
    const habil = proximoDiaDisponible(dia, { reglas, bloqueos });
    if (habil && habil !== dia) onDiaChange(habil);
  }

  if (profesionalId == null) {
    return (
      <p className="rounded-lg border border-dashed border-slate-200 px-3 py-6 text-center text-sm text-slate-500">
        Elige especialidad y profesional para ver su agenda.
      </p>
    );
  }

  if (sinAcceso) {
    return (
      <Alert variant="info">
        Esa agenda no es tuya para repartirla. Le da hora admisión.
      </Alert>
    );
  }

  if (!cargandoReglas && reglas.length === 0) {
    return (
      <Alert variant="info">
        Este profesional no tiene horario de atención configurado, así que no hay
        horas que ofrecer.
      </Alert>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-[auto_1fr]">
      <div>
        <CalendarioDisponibilidad
          value={dia}
          onChange={(f) => {
            onDiaChange(f);
            setMesVisible(f.slice(0, 7));
          }}
          disponibilidad={{ reglas, bloqueos, hoy }}
          marcasPorDia={marcasPorDia}
          onMesVisible={(a, m) =>
            setMesVisible(`${a}-${String(m + 1).padStart(2, "0")}`)
          }
        />
      </div>

      <div className="min-w-0">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Horas del día
        </p>
        {cargandoSlots && (
          <p className="py-6 text-center text-sm text-slate-400">Cargando…</p>
        )}
        {!cargandoSlots && franjas.length === 0 && (
          <p className="rounded-lg border border-dashed border-slate-200 px-3 py-6 text-center text-sm text-slate-500">
            Ese día no queda ningún hueco libre.
          </p>
        )}
        <ul className="max-h-72 space-y-1 overflow-auto pr-1">
          {franjas.map((f) => (
            <li key={`${f.inicio}-${f.ocupada ? "x" : "o"}`}>
              {f.ocupada ? (
                <div
                  className={`flex items-center gap-2 rounded-lg border-l-4 px-3 py-2 text-sm ${
                    f.sesion
                      ? "border-[color:var(--color-link)] bg-[color-mix(in_srgb,var(--color-link)_8%,white)] text-slate-700"
                      : f.pagada
                        ? "border-emerald-400 bg-emerald-50 text-slate-600"
                        : "border-amber-400 bg-amber-50 text-slate-600"
                  }`}
                >
                  <span className="font-mono">{horaDe(f.inicio, tz)}</span>
                  <span className="truncate">
                    {f.sesion ? `Sesión ${f.sesion} · ` : ""}
                    {f.quien}
                  </span>
                  <span className="ml-auto whitespace-nowrap text-xs font-medium">
                    {f.pagada ? "pagada" : "por cobrar"}
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => f.slot && onElegirSlot(f.slot)}
                  className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition ${
                    slotElegidoInicio === f.inicio
                      ? "border-[color:var(--color-link)] bg-[color:var(--color-link)] text-white"
                      : "border-slate-200 hover:border-[color:var(--color-link)] hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
                  }`}
                >
                  <span className="font-mono">{horaDe(f.inicio, tz)}</span>
                  <span
                    className={`text-xs ${
                      slotElegidoInicio === f.inicio ? "text-white/80" : "text-slate-500"
                    }`}
                  >
                    a {horaDe(f.fin, tz)}
                  </span>
                  <span
                    className={`ml-auto text-xs font-medium ${
                      slotElegidoInicio === f.inicio
                        ? "text-white"
                        : "text-[color:var(--color-link)]"
                    }`}
                  >
                    {slotElegidoInicio === f.inicio ? "Elegida" : "Elegir"}
                  </span>
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
